// src/profile/ProfileTickets.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import './tabs.css';

function groupTickets(tickets = []) {
  const map = new Map();
  for (const t of tickets) {
    const v = Number(t.value) || 0;
    const q = Number(t.quantity) || 0;
    map.set(v, (map.get(v) || 0) + q);
  }
  return [...map.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([value, qty]) => ({ value, qty }));
}

const styles = {
  card: {
    background: 'radial-gradient(circle at top left,#1f2937,#020617)',
    borderRadius: 20,
    padding: 24,
    border: '1px solid rgba(148,163,184,0.35)',
    boxShadow: '0 24px 60px rgba(0,0,0,0.7)',
    maxWidth: 840,
    color: '#e5e7eb',
    fontFamily:
      "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },

  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 24,
    marginBottom: 16,
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    borderRadius: 999,
    fontSize: 11,
    textTransform: 'uppercase',
    fontWeight: 600,
    letterSpacing: 0.4,
    background: 'rgba(96,165,250,0.08)',
    border: '1px solid rgba(96,165,250,0.5)',
    color: '#bfdbfe',
    marginBottom: 6,
  },
  title: {
    margin: 0,
    fontSize: 22,
    fontWeight: 700,
  },
  subtitle: {
    marginTop: 8,
    fontSize: 13,
    color: '#9ca3af',
    maxWidth: 460,
    lineHeight: 1.5,
  },

  ticketIconWrap: {
    minWidth: 140,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  ticketIcon: {
    width: 120,
    height: 80,
    borderRadius: 14,
    background:
      'linear-gradient(135deg,#1f2937 0%,#020617 40%,#111827 100%)',
    border: '1px solid rgba(59,130,246,0.6)',
    boxShadow: '0 0 0 3px rgba(15,23,42,0.9), 0 10px 25px rgba(0,0,0,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  ticketStripe: {
    position: 'absolute',
    inset: 0,
    background:
      'repeating-linear-gradient(135deg,rgba(37,99,235,0.35) 0,rgba(37,99,235,0.35) 8px,transparent 8px,transparent 16px)',
    opacity: 0.4,
  },
  ticketIconText: {
    position: 'relative',
    fontWeight: 800,
    fontSize: 22,
    letterSpacing: 1,
    color: '#e5e7eb',
    textShadow: '0 0 14px rgba(191,219,254,0.9)',
  },

  separator: {
    height: 1,
    background:
      'linear-gradient(90deg, transparent, rgba(148,163,184,0.4), transparent)',
    margin: '12px 0 16px',
  },

  infoTitle: {
    fontSize: 15,
    fontWeight: 600,
    marginBottom: 4,
  },
  infoText: {
    fontSize: 13,
    color: '#cbd5f5',
    maxWidth: 640,
    lineHeight: 1.6,
  },
  infoList: {
    marginTop: 8,
    fontSize: 13,
    color: '#9ca3af',
  },
  infoLi: { marginBottom: 4 },

  ticketsWrap: {
    marginTop: 16,
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))',
    gap: 12,
  },
  ticketCard: {
    borderRadius: 14,
    padding: 12,
    background:
      'radial-gradient(circle at top,#020617 0,#020617 40%,#020617 100%)',
    border: '1px solid rgba(55,65,81,0.9)',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  ticketRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
  },
  ticketTag: {
    padding: '4px 10px',
    borderRadius: 999,
    fontSize: 13,
    fontWeight: 700,
    background:
      'linear-gradient(135deg,#60a5fa 0%,#2563eb 40%,#1d4ed8 100%)',
    color: '#eff6ff',
    boxShadow: '0 0 10px rgba(37,99,235,0.6)',
  },
  ticketQty: {
    fontSize: 13,
    color: '#e5e7eb',
  },
  ticketHint: {
    fontSize: 12,
    color: '#9ca3af',
  },

  emptyText: { color: '#9aa3b2' },
};

export default function ProfileTickets({ tickets }) {
  const { t: translate } = useTranslation();
  const grouped = groupTickets(tickets);

  if (!grouped.length) {
    return <p style={styles.emptyText}>{translate('profile.tickets.empty')}</p>;
  }

  return (
    <div style={styles.card}>
      <div style={styles.headerRow}>
        <div>
          <div style={styles.badge}>{translate('profile.tickets.badge')}</div>
          <h2 style={styles.title}>{translate('profile.tickets.title')}</h2>
          <p style={styles.subtitle}>
            {translate('profile.tickets.subtitle')}
          </p>
        </div>

        {/* декоративный «тикет» справа */}
        <div style={styles.ticketIconWrap}>
          <div style={styles.ticketIcon}>
            <div style={styles.ticketStripe} />
            <span style={styles.ticketIconText}>TICKET</span>
          </div>
        </div>
      </div>

      <div style={styles.separator} />

      <div>
        <div style={styles.infoTitle}>{translate('profile.tickets.howTitle')}</div>
        <p style={styles.infoText}>
          {translate('profile.tickets.howText')}
        </p>

        <ul style={styles.infoList}>
          <li style={styles.infoLi}>
            🎟 <b>{translate('profile.tickets.bullets.paymentTitle')}</b>{' '}
            {translate('profile.tickets.bullets.paymentText')}
          </li>
          <li style={styles.infoLi}>
            ✅ <b>{translate('profile.tickets.bullets.questsTitle')}</b>{' '}
            {translate('profile.tickets.bullets.questsText')}
          </li>
          <li style={styles.infoLi}>
            ⭐ <b>{translate('profile.tickets.bullets.ratingTitle')}</b>{' '}
            {translate('profile.tickets.bullets.ratingText')}
          </li>
        </ul>
      </div>

      {/* список билетов игрока */}
      <div style={styles.ticketsWrap}>
        {grouped.map(({ value, qty }) => (
          <div key={value} style={styles.ticketCard}>
            <div style={styles.ticketRow}>
              <div style={styles.ticketTag}>{translate('profile.tickets.ticketLabel', { value })}</div>
              <div style={styles.ticketQty}>{translate('profile.common.quantity', { count: qty })}</div>
            </div>
            <div style={styles.ticketHint}>
              {translate('profile.tickets.ticketHint', { value })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// // src/profile/ProfileTickets.jsx
// import React from 'react';
// import './tabs.css';

// function groupTickets(tickets=[]) {
//   const map = new Map();
//   for (const t of tickets) {
//     const v = Number(t.value) || 0;
//     const q = Number(t.quantity) || 0;
//     map.set(v, (map.get(v) || 0) + q);
//   }
//   return [...map.entries()]
//     .sort((a,b)=>a[0]-b[0])
//     .map(([value, qty]) => ({ value, qty }));
// }

// export default function ProfileTickets({ tickets }) {
//   const grouped = groupTickets(tickets);
//   if (!grouped.length) return <p style={{color:'#9aa3b2'}}>Билетов нет.</p>;

//   return (
//     <div className="assets-list">
//       {grouped.map(({value, qty}) => (
//         <div key={value} className="asset-row">
//           <div className="asset-title">Билеты номиналом ${value}</div>
//           <div className="asset-qty">{qty} шт.</div>
//         </div>
//       ))}
//     </div>
//   );
// }
